import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  listStudyDocuments,
  deleteStudyDocument,
  uploadStudyDocument,
  saveStudyMarks,
  localMarkToServer,
  type StudyDocumentSummary,
} from "@/api/study";
import { listPdfs, loadPdf, loadMarks } from "@/create/lecture/lecture-pdf-store";
import { extractPdfText } from "@/create/lecture/extract-pdf-text";
import { Alert, Button, Spinner } from "@/components/ui";

// localStorage set of local IndexedDB refs already migrated to the server, so a
// document is never uploaded twice across library visits.
const MIGRATED_KEY = "study-migrated-refs";

function readMigrated(): Set<string> {
  try {
    const raw = localStorage.getItem(MIGRATED_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? (arr as string[]) : []);
  } catch {
    return new Set();
  }
}

function markMigrated(ref: string): void {
  try {
    const set = readMigrated();
    set.add(ref);
    localStorage.setItem(MIGRATED_KEY, JSON.stringify([...set]));
  } catch {
    /* localStorage unavailable — worst case we re-attempt (upload is idempotent-ish) */
  }
}

// One-time, transparent migration of the browser-only (IndexedDB) Study library
// to the server. For each local PDF not yet migrated: re-upload it (with fresh
// best-effort text extraction) and re-attach its marks, then record the ref.
// Every step is wrapped so one bad document never blocks the rest. The local
// IndexedDB entries are LEFT in place (harmless blob cache).
async function migrateLocalStudyDocs(): Promise<void> {
  let locals: Awaited<ReturnType<typeof listPdfs>>;
  try {
    locals = await listPdfs();
  } catch {
    return;
  }
  const migrated = readMigrated();
  for (const meta of locals) {
    if (migrated.has(meta.ref)) continue;
    try {
      const blob = await loadPdf(meta.ref);
      if (!blob) continue; // orphaned meta, nothing to upload — retry-safe (unmarked)
      const name = meta.name || "document";
      const file = new File([blob], `${name}.pdf`, { type: "application/pdf" });

      let extractedText = "";
      try {
        extractedText = await extractPdfText(file);
      } catch {
        /* scanned / no extractable text — snips still drive marking */
      }

      const doc = await uploadStudyDocument({ file, filename: name, extractedText });

      // Re-attach marks if the local document had any.
      const local = await loadMarks<{
        deep?: Record<string, unknown>[];
        lookups?: Record<string, unknown>[];
        highlights?: Record<string, unknown>[];
      }>(meta.ref);
      const count =
        (local?.deep?.length ?? 0) +
        (local?.lookups?.length ?? 0) +
        (local?.highlights?.length ?? 0);
      if (local && count > 0) {
        await saveStudyMarks(doc.id, {
          deep: (local.deep ?? []).map(localMarkToServer),
          lookups: (local.lookups ?? []).map(localMarkToServer),
          highlights: (local.highlights ?? []).map(localMarkToServer),
        });
      }

      markMigrated(meta.ref);
    } catch {
      /* one failed migration mustn't block the rest — leave it unmarked to retry */
    }
  }
}

// Landing for the standalone Study flow: the learner's server-persisted marked
// PDFs (reopen from any device). Auto-migrates any legacy browser-only PDFs on
// first visit, transparently.
export function StudyLibrary() {
  const { t } = useTranslation();
  const [items, setItems] = useState<StudyDocumentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const rows = await listStudyDocuments();
      setItems(rows);
    } catch {
      setItems([]);
      setError(t("Could not load your library."));
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      await migrateLocalStudyDocs();
      if (!cancelled) await refresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const remove = async (id: string) => {
    if (!window.confirm(t("Remove this PDF and its marks from your library?"))) return;
    try {
      await deleteStudyDocument(id);
    } catch {
      /* fall through to refresh — a stale row just reappears */
    }
    void refresh();
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("Mark & study")}</h1>
          <p className="mt-1 text-ink-faint">
            {t("Read and mark your own PDFs — explanations on tap, no question set required.")}
          </p>
        </div>
        <Link to="/app/study/new">
          <Button>{t("Mark a new PDF")}</Button>
        </Link>
      </div>

      {error && (
        <div className="mt-6">
          <Alert>{error}</Alert>
        </div>
      )}

      {items === null && (
        <div className="flex justify-center py-20 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}

      {items !== null && items.length === 0 && (
        <div className="card mt-8 p-8 text-center">
          <p className="text-ink-faint">
            {t("No marked PDFs yet. Pick one to start reading + marking.")}
          </p>
          <Link to="/app/study/new" className="mt-4 inline-block">
            <Button>{t("Mark a new PDF")}</Button>
          </Link>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((it) => (
            <div
              key={it.id}
              className="card group flex flex-col p-5 transition hover:-translate-y-0.5 hover:shadow-glow"
            >
              <Link to={`/app/study/${it.id}`} className="flex-1">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-2xl">
                  📄
                </div>
                <h2 className="mt-4 truncate font-semibold text-ink group-hover:text-brand-700">
                  {it.filename}
                </h2>
                <p className="mt-1 text-xs text-ink-faint">
                  {it.mark_count
                    ? (it.mark_count === 1
                        ? t("{{count}} mark", { count: it.mark_count })
                        : t("{{count}} marks", { count: it.mark_count })) + " · "
                    : ""}
                  {new Date(it.last_opened_at ?? it.created_at).toLocaleDateString()}
                </p>
              </Link>
              <button
                onClick={() => void remove(it.id)}
                className="mt-3 self-start text-xs text-red-500 hover:underline"
              >
                {t("Remove")}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
